// ============================================================================
// FORMATO REGIONAL — CONFIGURACIÓN CANÓNICA (módulo hoja, sin dependencias)
// ============================================================================
// FUENTE ÚNICA de los locales soportados y del default del sistema.
//
// Por qué existe este archivo
// ---------------------------
// Antes había DOS defaults contradictorios:
//   utils/auth.js            → DEFAULT_LOCALE = 'en-US'
//   utils/formatProjectData.js → GLOBAL_LOCALE = 'es-CL'
// Mientras el locale no se aplicaba, la contradicción era invisible. Al
// conectarlo (Fase 1) producía un bug silencioso: un proyecto SIN Formato
// Regional configurado mostraba coma decimal en unos componentes y punto en
// otros, en la misma pantalla.
//
// Este módulo NO importa nada a propósito: así puede ser importado tanto por
// auth.js como por formatProjectData.js sin riesgo de import circular.
//
// Debe mantenerse sincronizado con:
//   - PROJECT_LOCALES en api/models/Proyects.js
//   - formatosRegionales en components/administracion/Proyectos.vue

/** Locales que el selector "Formato Regional" ofrece y la API acepta. */
export const SUPPORTED_LOCALES = ['es-CL', 'en-US'];

/**
 * Default del sistema cuando el proyecto no tiene Formato Regional
 * configurado (`proyectos.locale IS NULL`).
 *
 * Se eligió 'es-CL' porque es lo que la aplicación viene mostrando de facto
 * (GLOBAL_LOCALE de formatProjectData.js) y porque la totalidad de los
 * proyectos actuales son chilenos. Cambiar este valor cambia cómo se ven los
 * proyectos sin configurar; es el ÚNICO lugar donde hay que tocarlo.
 */
export const DEFAULT_LOCALE = 'es-CL';

/**
 * Etiqueta legible de cada locale, para el selector "Formato Regional" de
 * Administración > Proyectos. Vive acá y no en el componente, que antes tenía
 * su propia lista y podía desincronizarse.
 *
 * OJO: agregar un locale a SUPPORTED_LOCALES NO alcanza. Hay cinco tablas
 * paralelas indexadas por locale y sin su entrada el locale nuevo se degrada
 * EN SILENCIO a los defaults:
 *   LOCALE_LABELS      (acá)                  -> el selector muestra el código
 *   DAYJS_LOCALES      (acá)                  -> nombres de mes/día
 *   DATE_FORMATS       (formatProjectDate.js) -> orden de los campos
 *   HIGHCHARTS_LOCALES (highchartsLocale.js)  -> gráficos
 *   FLATPICKR_L10N     (flatpickrLocale.js)   -> calendarios
 */
export const LOCALE_LABELS = {
  'es-CL': 'Español (Chile)',
  'en-US': 'Inglés (Estados Unidos)'
};

/** Opciones `{ value, text }` del selector de Formato Regional. */
export function localeOptions() {
  return SUPPORTED_LOCALES.map((value) => ({ value, text: LOCALE_LABELS[value] || value }));
}

/** Locales de Day.js registrados en utils/dayjs.js. */
const DAYJS_LOCALES = { 'es-CL': 'es', 'en-US': 'en' };

/** ¿El valor es uno de los locales soportados? */
export function isSupportedLocale(locale) {
  return SUPPORTED_LOCALES.includes(String(locale ?? '').trim());
}

/** Normaliza cualquier entrada a un locale soportado (o al default). */
export function resolveLocale(locale) {
  const normalized = String(locale ?? '').trim();
  return isSupportedLocale(normalized) ? normalized : DEFAULT_LOCALE;
}

/** Locale de Day.js correspondiente ('es-CL' → 'es'). */
export function dayjsLocaleFor(locale) {
  return DAYJS_LOCALES[resolveLocale(locale)] ?? 'es';
}

/** Separador decimal del locale, derivado de Intl (no hardcodeado). */
export function decimalSeparatorFor(locale) {
  return new Intl.NumberFormat(resolveLocale(locale))
    .formatToParts(1.1).find((p) => p.type === 'decimal')?.value ?? ',';
}

/** Separador de miles del locale, derivado de Intl (no hardcodeado). */
export function groupSeparatorFor(locale) {
  return new Intl.NumberFormat(resolveLocale(locale))
    .formatToParts(1000).find((p) => p.type === 'group')?.value ?? '.';
}

export default {
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  LOCALE_LABELS,
  localeOptions,
  isSupportedLocale,
  resolveLocale,
  dayjsLocaleFor,
  decimalSeparatorFor,
  groupSeparatorFor
};
